/* eslint-disable @typescript-eslint/no-unused-vars */
import type { BaseEntity } from '@aibees/shared/definition';
import { EXPORT_SIZE_LIMIT } from '@aibees/shared/features/advance-table/components/advanced-table-select-popup/constant';
import { concat, groupBy, uniq } from 'lodash-es';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { TableSelectionProps } from './types';

type ReturnType<T extends BaseEntity> = TableSelectionProps<T>;

export type UseTableSelectionProps = {
  defaultAdd?: string[];
  defaultInclude?: string[];
  defaultExclude?: string[];
};

export function selectionCount(
  added: string[],
  included: string[],
  excluded: string[],
  topSelected: string[],
  size: number
) {
  const selected = uniq(concat(added, included));
  const calculatedCount = size
    ? (size + selected.length > EXPORT_SIZE_LIMIT
        ? EXPORT_SIZE_LIMIT
        : size + selected.length) - excluded.length
    : selected.length + topSelected.length;
  return calculatedCount;
}

export function useTableSelection<T extends BaseEntity>(
  props?: UseTableSelectionProps
): ReturnType<T> {
  const defaultAdd = props?.defaultAdd;
  const defaultInclude = props?.defaultInclude;
  const defaultExclude = props?.defaultExclude;

  const [size, setSize] = useState<number>(0);
  const [topSelected, setTopSelected] = useState<string[]>([]);
  const [topIds, setTopIds] = useState<string[]>([]);
  const [added, setAdded] = useState<string[]>(defaultAdd ?? []);
  const [included, setIncluded] = useState<string[]>(defaultInclude ?? []);
  const [excluded, setExcluded] = useState<string[]>(defaultExclude || []);
  const [items, setItems] = useState<T[]>([]);
  const [extraParam, setExtraParam] = useState<Record<string, any>>();

  // Refs hold latest state so callbacks can have stable identity (empty deps)
  const addedRef = useRef(added);
  const includedRef = useRef(included);
  const excludedRef = useRef(excluded);
  const topIdsRef = useRef(topIds);
  const topSelectedRef = useRef(topSelected);
  const defaultAddRef = useRef(defaultAdd);
  const defaultIncludeRef = useRef(defaultInclude);
  const extraParamRef = useRef(extraParam);
  const sizeRef = useRef(size);

  addedRef.current = added;
  includedRef.current = included;
  excludedRef.current = excluded;
  topIdsRef.current = topIds;
  topSelectedRef.current = topSelected;
  defaultAddRef.current = defaultAdd;
  defaultIncludeRef.current = defaultInclude;
  extraParamRef.current = extraParam;
  sizeRef.current = size;

  const appendInitialIds = useCallback((ids?: string[]) => {
    ids ||= [];
    const exc = excludedRef.current;
    setTopSelected(ids.filter((i) => !exc.includes(i)));
    setTopIds(ids);
    if (!extraParamRef.current) setExtraParam([]);
  }, []);

  const onSelectRowsBySize = useCallback((value: number) => {
    setSize(value);
    setAdded(defaultAddRef.current ?? []);
    setTopSelected(topIdsRef.current);
    setExcluded([]);
    setIncluded([]);
    setItems([]);
  }, []);

  const onSelectRowsById = useCallback((rows?: T[], value?: number) => {
    setSize(0);
    setIncluded(() => {
      if (value && Number(value) > 0) {
        type RowWithCompany = T & { company?: { id?: string } };
        const rowsWithCompany = (rows ?? []) as RowWithCompany[];
        const groups = groupBy(
          rowsWithCompany.filter(
            (x) => x.company?.id || x.positionGroups?.[0]?.company?.id
          ),
          (x) => x.company?.id || x.positionGroups?.[0]?.company?.id || ''
        );
        const selectedItems = Object.values(groups).flatMap((x) =>
          x.slice(0, Number(value))
        );
        return concat(
          defaultIncludeRef.current ?? [],
          selectedItems?.map((x) => x.id) ?? []
        );
      }
      return concat(defaultAddRef.current ?? [], rows?.map((x) => x.id) ?? []);
    });
    setExcluded([]);
    setAdded(defaultAddRef.current ?? []);
    setTopSelected(topIdsRef.current);
    if (rows) setItems(rows);
    else setItems([]);
  }, []);

  const selectRowWithSize = useCallback(
    (index: number, id: string, checked: boolean, item?: T) => {
      const add = addedRef.current;
      const exc = excludedRef.current;
      const sz = sizeRef.current;
      const tIds = topIdsRef.current;

      if (!checked) {
        if (add.includes(id)) setAdded(add.filter((x) => x !== id));
        setItems((prev) => {
          if (item) return prev.filter((x) => x.id !== item.id);
          return prev;
        });
        if (index < sz - tIds.length) setExcluded(concat(exc, [id]));
      } else {
        if (exc.includes(id)) setExcluded(exc.filter((x) => x !== id));
        if (index >= sz - tIds.length) setAdded(concat(add, [id]));
        setItems((prev) => {
          if (item) return [...prev, item];
          return prev;
        });
      }
    },
    []
  );

  const selectRowWithoutSize = useCallback(
    (id: string, checked: boolean, item?: T) => {
      const inc = includedRef.current;
      const exc = excludedRef.current;

      if (!checked) {
        setItems((prev) => {
          if (item) return prev.filter((x) => x.id !== item.id);
          return prev;
        });
        if (inc.includes(id)) setIncluded(inc.filter((x) => x !== id));
      } else {
        if (exc.includes(id)) setExcluded(exc.filter((x) => x !== id));
        setIncluded(concat(inc, [id]));
        setItems((prev) => {
          if (item) return [...prev, item];
          return prev;
        });
      }
    },
    []
  );

  const onSelectTopRow = useCallback((id: string) => {
    const top = topSelectedRef.current;
    const exc = excludedRef.current;
    if (!top.includes(id)) {
      setTopSelected(concat(top, [id]));
      setExcluded(exc.filter((x) => x !== id));
    } else {
      setTopSelected(top.filter((x) => x !== id));
      setExcluded(concat(exc, [id]));
    }
  }, []);

  const onSetExtraParam = useCallback((key: string, value: any) => {
    setExtraParam((prev) => ({ ...prev, [key]: value }));
  }, []);

  const onRemoveExtraParam = useCallback((key: string) => {
    setExtraParam((prev) => {
      if (!prev) return prev;
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const isRowSelected = useCallback((index: number, id: string) => {
    const add = addedRef.current;
    const inc = includedRef.current;
    const sz = sizeRef.current;
    const tIds = topIdsRef.current;
    const exc = excludedRef.current;
    return (
      add.includes(id) ||
      inc.includes(id) ||
      (!!sz && index < sz - tIds.length && !exc.includes(id))
    );
  }, []);

  const isTopRowSelected = useCallback((id: string) => {
    return topSelectedRef.current.includes(id);
  }, []);

  const onClear = useCallback(() => {
    setSize(0);
    setAdded(defaultAddRef.current ?? []);
    setTopSelected(topIdsRef.current);
    setIncluded(defaultIncludeRef.current ?? []);
    setExcluded([]);
    setItems([]);
  }, []);

  // Sync state from props when defaultAdd/defaultInclude change
  useLayoutEffect(() => {
    setAdded(defaultAdd ?? []);
  }, [defaultAdd]);

  useLayoutEffect(() => {
    setIncluded(defaultInclude ?? []);
  }, [defaultInclude]);

  const count = useMemo(
    () => selectionCount(added, included, excluded, topSelected, size),
    [added, included, excluded, topSelected, size]
  );

  const onSelectRow = useCallback(
    (i: number, id: string, checked: boolean, item?: T) => {
      if (sizeRef.current) {
        selectRowWithSize(i, id, checked, item);
      } else {
        selectRowWithoutSize(id, checked, item);
      }
    },
    [selectRowWithSize, selectRowWithoutSize]
  );

  return useMemo(
    () => ({
      size,
      extraParam,
      count,
      added,
      setAdded,
      included,
      setIncluded,
      appendInitialIds,
      excluded,
      setExcluded,
      items,
      setItems,
      onSelectRowsBySize,
      onSelectRow,
      onSelectTopRow,
      onSelectRowsById,
      onSetExtraParam,
      onRemoveExtraParam,
      isRowSelected,
      isTopRowSelected,
      onClear,
      topSelected,
    }),
    [
      size,
      extraParam,
      count,
      added,
      included,
      excluded,
      items,
      topSelected,
      appendInitialIds,
      onSelectRowsBySize,
      onSelectRow,
      onSelectTopRow,
      onSelectRowsById,
      onSetExtraParam,
      onRemoveExtraParam,
      isRowSelected,
      isTopRowSelected,
      onClear,
    ]
  );
}
